import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { AssessmentsAPI } from "@/lib/api";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";

const DIM_KEYS = [
  { key: "brand_alignment", label: "Brand Alignment" },
  { key: "ecommerce_shelf", label: "E-commerce Shelf" },
  { key: "aisle_resiliency", label: "Aisle Resiliency" },
  { key: "traditional_retail_india", label: "Traditional Retail" },
  { key: "distinctiveness", label: "Distinctiveness" },
  { key: "hierarchy_eye_tracking", label: "Hierarchy & Eye" },
  { key: "audience_fit", label: "Audience Fit" },
];

export default function AssessmentDiff() {
  const { id } = useParams();
  const [a, setA] = useState(null);

  useEffect(() => {
    AssessmentsAPI.get(id, { with_compare: 1, lite: 1 }).then((r) => setA(r.data));
  }, [id]);

  const cur = useMemo(() => indexByKey(a?.dimensions), [a?.dimensions]);
  const prev = useMemo(() => indexByKey(a?.compare_against?.dimensions), [a?.compare_against?.dimensions]);

  if (!a) return <div className="v-container" style={{ paddingTop: 80 }}><div className="overline">Loading…</div></div>;
  if (!a.compare_against) {
    return (
      <div className="v-container" style={{ paddingTop: 80 }}>
        <div className="overline">No comparison</div>
        <p style={{ marginTop: 12, color: "var(--v-text-2)" }}>This assessment isn't comparing against a parent pack. <Link to={`/assessments/${id}`} className="v-link">Open the assessment</Link>.</p>
      </div>
    );
  }

  const radarData = DIM_KEYS.map((d) => ({
    subject: d.label,
    current: Number(cur[d.key]?.score || 0),
    previous: Number(prev[d.key]?.score || 0),
  }));

  const delta = (Number(a.overall_score || 0) - Number(a.compare_against.overall_score || 0));

  return (
    <div className="v-container" style={{ paddingTop: 48, paddingBottom: 96 }}>
      <Link to={`/assessments/${id}`} className="overline" data-testid="back-to-assessment">← Assessment</Link>

      <div style={{ marginTop: 16 }}>
        <div className="overline">Side-by-side diff</div>
        <h1 className="display" style={{ fontSize: "clamp(36px,4.5vw,60px)", lineHeight: 1, marginTop: 10 }}>
          {a.title} <span style={{ color: "var(--v-text-3)" }}>vs</span> {a.compare_against.title}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" style={{ marginTop: 36 }}>
        <ScoreHero label="Previous pack" score={a.compare_against.overall_score} sub={`by ${a.compare_against.designer_name || "—"}`} testId="diff-prev-score" />
        <DeltaHero delta={delta} />
        <ScoreHero label="This pack" score={a.overall_score} sub={`by ${a.designer_name || "—"}`} accent testId="diff-cur-score" />
      </div>

      <div className="v-surface" style={{ padding: 24, marginTop: 32 }} data-testid="diff-radar">
        <div className="overline">Radar overlay</div>
        <div style={{ height: 360, marginTop: 12 }}>
          <ResponsiveContainer>
            <RadarChart data={radarData}>
              <PolarGrid stroke="var(--v-border)" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: "var(--v-text-2)", fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 10]} tick={{ fill: "var(--v-text-3)", fontSize: 10 }} />
              <Radar name="Previous" dataKey="previous" stroke="var(--v-text-3)" fill="var(--v-text-3)" fillOpacity={0.18} />
              <Radar name="This pack" dataKey="current" stroke="var(--v-accent)" fill="var(--v-accent)" fillOpacity={0.35} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <Legend />
      </div>

      <div style={{ marginTop: 32, display: "grid", gap: 16 }}>
        {DIM_KEYS.map((d, i) => {
          const c = cur[d.key];
          const p = prev[d.key];
          if (!c && !p) return null;
          const cs = Number(c?.score || 0);
          const ps = Number(p?.score || 0);
          const diff = cs - ps;
          return (
            <div key={d.key} className="v-surface" style={{ padding: 24 }} data-testid={`diff-dim-${d.key}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div className="mono" style={{ color: "var(--v-text-3)", fontSize: 11 }}>{String(i + 1).padStart(2, "0")}</div>
                  <h3 style={{ marginTop: 4, fontSize: 22 }}>{c?.label || p?.label || d.label}</h3>
                </div>
                <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                  <div style={{ textAlign: "right" }}>
                    <div className="mono" style={{ fontSize: 10, color: "var(--v-text-3)" }}>Previous</div>
                    <div className="v-score" style={{ fontSize: 34, color: "var(--v-text-3)" }}>{ps.toFixed(1)}</div>
                  </div>
                  <div style={{ width: 28, textAlign: "center", color: "var(--v-text-3)" }}>→</div>
                  <div style={{ textAlign: "right" }}>
                    <div className="mono" style={{ fontSize: 10, color: "var(--v-accent)" }}>This pack</div>
                    <div className="v-score v-gradient-text" style={{ fontSize: 44 }}>{cs.toFixed(1)}</div>
                  </div>
                  <DeltaBadge value={diff} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" style={{ marginTop: 18 }}>
                <Panel label="Previous · weaknesses" items={p?.weaknesses} muted />
                <Panel label="This pack · weaknesses" items={c?.weaknesses} />
                <Panel label="Previous · improvements" items={p?.improvements} muted />
                <Panel label="This pack · improvements" items={c?.improvements} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function indexByKey(arr) {
  const m = {};
  (arr || []).forEach((d) => { m[d.key] = d; });
  return m;
}

function ScoreHero({ label, score, sub, accent, testId }) {
  return (
    <div className="v-surface" style={{ padding: 24 }} data-testid={testId}>
      <div className="overline">{label}</div>
      <div className={`v-score${accent ? " v-gradient-text" : ""}`} style={{ fontSize: 72, marginTop: 6, color: accent ? undefined : "var(--v-text)" }}>
        {Number(score || 0).toFixed(1)}
      </div>
      <div className="mono" style={{ fontSize: 11, color: "var(--v-text-3)", marginTop: 4 }}>{sub}</div>
    </div>
  );
}

function DeltaHero({ delta }) {
  const positive = delta >= 0;
  return (
    <div className="v-surface" style={{ padding: 24, textAlign: "center", borderColor: positive ? "var(--v-accent)" : "var(--v-border)" }} data-testid="diff-delta-hero">
      <div className="overline" style={{ color: positive ? "var(--v-accent)" : "var(--v-text-2)" }}>Net move</div>
      <div className="v-score" style={{ fontSize: 72, marginTop: 6, color: positive ? "var(--v-accent)" : "var(--v-text-2)" }}>
        {positive ? "+" : ""}{Number(delta).toFixed(1)}
      </div>
      <div className="mono" style={{ fontSize: 11, color: "var(--v-text-3)", marginTop: 4 }}>overall verdict shift</div>
    </div>
  );
}

function DeltaBadge({ value }) {
  const v = Number(value);
  const positive = v >= 0;
  return (
    <div className="v-chip" style={{ borderColor: positive ? "var(--v-accent)" : "var(--v-border)", color: positive ? "var(--v-accent)" : "var(--v-text-2)", background: positive ? "color-mix(in srgb, var(--v-accent) 12%, transparent)" : "transparent" }} data-testid="diff-badge">
      {positive ? "+" : ""}{v.toFixed(1)}
    </div>
  );
}

function Panel({ label, items, muted }) {
  return (
    <div>
      <div className="overline" style={{ color: muted ? "var(--v-text-3)" : "var(--v-text-2)" }}>{label}</div>
      <ul style={{ marginTop: 6, paddingLeft: 16, lineHeight: 1.55, fontSize: 13, color: muted ? "var(--v-text-3)" : "var(--v-text)" }}>
        {(items || []).map((it, i) => <li key={`${i}-${typeof it === "string" ? it.slice(0, 24) : i}`}>{it}</li>)}
        {(!items || items.length === 0) && <li style={{ color: "var(--v-text-3)" }}>—</li>}
      </ul>
    </div>
  );
}

function Legend() {
  return (
    <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "center" }}>
      <span className="mono" style={{ fontSize: 11, color: "var(--v-text-3)", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, background: "var(--v-text-3)", borderRadius: 2 }} /> Previous
      </span>
      <span className="mono" style={{ fontSize: 11, color: "var(--v-accent)", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 10, height: 10, background: "var(--v-accent)", borderRadius: 2 }} /> This pack
      </span>
    </div>
  );
}
